import React from 'react';
import { Card, CardContent } from '../ui/card';
import {
  StatCard,
  ProgressBar,
  DemographicsPieChart,
  AgeDemographicsPieChart,
  FootfallBarChart,
  TimeSeriesChart,
} from './MetricCharts';

interface MetricsData {
  enabledMetrics?: string[];
  numberOfMachines?: number;
  numberOfLocations?: number;
  footfallPerAnnum?: number;
  impressionsPerMachine?: number;
  machineSpecs?: Record<string, any>;
  abc1Percentage?: number;
  averageDwellTimeMinutes?: number;
  genderFemale?: number;
  genderMale?: number;
  averageSpendPerVisitCents?: number;
  weeklyFootfall?: number;
  weeklyFootfallBreakdown?: Record<string, number>;
  ageDemographics?: Record<string, number>;
  weeklyExpectedSales?: number;
  monthlyMachineInteractions?: number;
  weeklyMachineInteractions?: number;
  dailyMachineInteractions?: number;
  locationBreakdown?: Array<{ name: string; machineCount: number }>;
}

interface MetricDisplayProps {
  metrics: MetricsData;
}

export function MetricDisplay({ metrics }: MetricDisplayProps) {
  const { enabledMetrics = [] } = metrics;

  if (enabledMetrics.length === 0) {
    return null;
  }

  // Group metrics logically
  const highlightedStats = [];
  const primaryStats = [];
  const demographics = [];
  const timeSeries = [];
  const otherMetrics = [];

  // Key metrics to highlight: numberOfMachines, numberOfLocations, footfallPerAnnum
  if (enabledMetrics.includes('numberOfMachines') && metrics.numberOfMachines !== undefined) {
    highlightedStats.push('numberOfMachines');
  }
  if (enabledMetrics.includes('numberOfLocations') && metrics.numberOfLocations !== undefined) {
    highlightedStats.push('numberOfLocations');
  }
  if (enabledMetrics.includes('footfallPerAnnum') && metrics.footfallPerAnnum !== undefined) {
    highlightedStats.push('footfallPerAnnum');
  }

  // Other primary stats
  if (enabledMetrics.includes('impressionsPerMachine') && metrics.impressionsPerMachine !== undefined) {
    primaryStats.push('impressionsPerMachine');
  }
  if (enabledMetrics.includes('weeklyExpectedSales') && metrics.weeklyExpectedSales !== undefined) {
    primaryStats.push('weeklyExpectedSales');
  }
  if (enabledMetrics.includes('averageSpendPerVisitCents') && metrics.averageSpendPerVisitCents !== undefined) {
    primaryStats.push('averageSpendPerVisitCents');
  }
  if (enabledMetrics.includes('averageDwellTimeMinutes') && metrics.averageDwellTimeMinutes !== undefined) {
    primaryStats.push('averageDwellTimeMinutes');
  }

  // Demographics
  if (enabledMetrics.includes('genderFemale') && enabledMetrics.includes('genderMale')) {
    demographics.push('gender');
  }
  if (enabledMetrics.includes('ageDemographics') && metrics.ageDemographics) {
    demographics.push('ageDemographics');
  }
  if (enabledMetrics.includes('abc1Percentage') && metrics.abc1Percentage !== undefined) {
    demographics.push('abc1Percentage');
  }

  // Time series
  if (enabledMetrics.includes('weeklyFootfall')) {
    timeSeries.push('weeklyFootfall');
  }
  if (enabledMetrics.includes('dailyMachineInteractions') || 
      enabledMetrics.includes('weeklyMachineInteractions') || 
      enabledMetrics.includes('monthlyMachineInteractions')) {
    timeSeries.push('machineInteractions');
  }

  // Other metrics
  if (enabledMetrics.includes('machineSpecs') && metrics.machineSpecs) {
    otherMetrics.push('machineSpecs');
  }

  return (
    <div className="space-y-4">
      {/* Highlighted Key Metrics Section */}
      {highlightedStats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {highlightedStats.includes('numberOfMachines') && (
            <StatCard
              title="Number of Machines"
              value={metrics.numberOfMachines!}
              format="number"
              highlighted={true}
            />
          )}
          {highlightedStats.includes('numberOfLocations') && (
            <StatCard
              title="Number of Locations"
              value={metrics.numberOfLocations!}
              format="number"
              highlighted={true}
              locationBreakdown={metrics.locationBreakdown}
            />
          )}
          {highlightedStats.includes('footfallPerAnnum') && (
            <StatCard
              title="Footfall per Annum"
              value={metrics.footfallPerAnnum!}
              format="number"
              highlighted={true}
            />
          )}
        </div>
      )}

      {/* Other Primary Stats Section */}
      {primaryStats.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {primaryStats.includes('impressionsPerMachine') && (
            <StatCard
              title="Impressions per Machine per Month"
              value={metrics.impressionsPerMachine!}
              format="number"
            />
          )}
          {primaryStats.includes('weeklyExpectedSales') && (
            <StatCard
              title="Weekly Expected Sales"
              value={metrics.weeklyExpectedSales!}
              format="currency"
            />
          )}
          {primaryStats.includes('averageSpendPerVisitCents') && (
            <StatCard
              title="Average Spend per Visit"
              value={metrics.averageSpendPerVisitCents! / 100}
              format="currency"
            />
          )}
          {primaryStats.includes('averageDwellTimeMinutes') && (
            <StatCard
              title="Average Dwell Time"
              value={metrics.averageDwellTimeMinutes!}
              format="time"
            />
          )}
        </div>
      )}

      {/* Demographics Section */}
      {demographics.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {demographics.includes('gender') && metrics.genderFemale !== undefined && metrics.genderMale !== undefined && (
            <DemographicsPieChart
              female={metrics.genderFemale}
              male={metrics.genderMale}
            />
          )}
          {demographics.includes('ageDemographics') && (
            <AgeDemographicsPieChart data={metrics.ageDemographics!} />
          )}
          {demographics.includes('abc1Percentage') && (
            <ProgressBar
              title="ABC1 Percentage"
              value={metrics.abc1Percentage!}
              max={100}
              format="percentage"
            />
          )}
        </div>
      )}

      {/* Time Series Section */}
      {timeSeries.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {timeSeries.includes('weeklyFootfall') && (
            <FootfallBarChart
              data={metrics.weeklyFootfallBreakdown || {}}
              total={metrics.weeklyFootfall}
            />
          )}
          {timeSeries.includes('machineInteractions') && (
            <TimeSeriesChart
              title="Interactions Per Machine"
              daily={metrics.dailyMachineInteractions}
              weekly={metrics.weeklyMachineInteractions}
              monthly={metrics.monthlyMachineInteractions}
            />
          )}
        </div>
      )}

      {/* Other Metrics */}
      {otherMetrics.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {otherMetrics.includes('machineSpecs') && (
            <Card style={{ boxShadow: '0 4px 6px -1px rgba(24, 61, 247, 0.3), 0 2px 4px -1px rgba(24, 61, 247, 0.18)' }}>
              <CardContent className="pt-3 pb-3">
                <p className="text-xs font-medium text-brand-navy mb-3 text-center">Machine Specifications</p>
                <div className="grid grid-cols-3 gap-0 divide-x divide-muted">
                  {Object.entries(metrics.machineSpecs!).map(([key, value], index) => (
                    <div 
                      key={key} 
                      className="flex flex-col items-center justify-center py-3 px-4 first:pl-0 last:pr-0"
                    >
                      <p className="text-xs font-medium text-brand-navy mb-1.5 capitalize text-center">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                      <p className="text-sm font-semibold text-brand-primary text-center">{String(value)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

